// <s-announcement-rotator> crossfades [data-announcement] slides with prev/next buttons.
// Auto-advance pauses on hover/focus and never runs under reduced motion.
class AnnouncementRotator extends HTMLElement {
  #slides = [];
  #index = 0;
  #timer = 0;
  #paused = { hover: false, focus: false, hidden: false, motion: false };
  #controller = null;

  connectedCallback() {
    this.#slides = Array.from(this.querySelectorAll('[data-announcement]'));
    const buttons = this.querySelectorAll('[data-announcement-prev], [data-announcement-next]');
    if (this.#slides.length < 2) {
      buttons.forEach((button) => { button.hidden = true; });
      return;
    }

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#paused.motion = motionQuery.matches;

    motionQuery.addEventListener('change', (event) => { this.#paused.motion = event.matches; this.#sync(); }, { signal });
    this.addEventListener('pointerenter', () => { this.#paused.hover = true; this.#sync(); }, { signal });
    this.addEventListener('pointerleave', () => { this.#paused.hover = false; this.#sync(); }, { signal });
    this.addEventListener('focusin', () => { this.#paused.focus = true; this.#sync(); }, { signal });
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) { this.#paused.focus = false; this.#sync(); }
    }, { signal });
    document.addEventListener('visibilitychange', () => { this.#paused.hidden = document.hidden; this.#sync(); }, { signal });
    this.querySelector('[data-announcement-prev]')?.addEventListener('click', () => this.#show(this.#index - 1), { signal });
    this.querySelector('[data-announcement-next]')?.addEventListener('click', () => this.#show(this.#index + 1), { signal });

    this.#sync();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    window.clearInterval(this.#timer);
  }

  #sync() {
    window.clearInterval(this.#timer);
    if (Object.values(this.#paused).some(Boolean)) return;
    const interval = Number(this.dataset.interval) || 5000;
    this.#timer = window.setInterval(() => this.#show(this.#index + 1), interval);
  }

  #show(nextIndex) {
    const count = this.#slides.length;
    const current = this.#slides[this.#index];
    this.#index = (nextIndex + count) % count;
    const next = this.#slides[this.#index];
    if (current === next) return;
    current.classList.add('opacity-0');
    current.setAttribute('aria-hidden', 'true');
    next.classList.remove('opacity-0');
    next.removeAttribute('aria-hidden');
  }
}

if (!customElements.get('s-announcement-rotator')) customElements.define('s-announcement-rotator', AnnouncementRotator);
